import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Sparkles, Database, ArrowRight } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  const DistillLogo = ({ size = 'default' }) => {
    const h = size === 'small' ? 'h-14' : 'h-20';
    return (
      <img src="/distill-logo.png" alt="Distill" className={h} />
    );
  };

  return (
    <div className="min-h-screen bg-[#080812] text-[#FAFAF8] font-['Inter',sans-serif] overflow-x-hidden" data-testid="landing-page">
      {/* Sticky Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#080812]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div data-testid="landing-logo">
            <DistillLogo />
          </div>

          <button
            onClick={() => navigate('/auth')}
            className="px-5 py-2 text-sm font-medium border border-white/15 rounded-md hover:bg-white/5 transition-colors"
            data-testid="nav-login-btn"
          >
            Log In
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-40 pb-24 px-6" data-testid="hero-section">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#AAFF00]/20 bg-[#AAFF00]/5 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#AAFF00] animate-pulse" />
            <span className="text-xs font-medium text-[#AAFF00] tracking-wide uppercase">Now in Early Access</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05] mb-6">
            The Company Brain<br />
            <span className="text-[#AAFF00]">your business is missing.</span>
          </h1>

          <p className="text-lg sm:text-xl text-[#FAFAF8]/60 max-w-2xl mx-auto mb-10 leading-relaxed">
            Distill captures what your team knows — while they work — and turns it into structured knowledge that never leaves.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/auth')}
              className="group flex items-center gap-2 px-8 py-3.5 bg-[#AAFF00] text-[#080812] font-semibold rounded-md hover:bg-[#c4ff4d] transition-all text-base"
              data-testid="hero-cta-btn"
            >
              Get Early Access
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/auth')}
              className="px-8 py-3.5 text-[#FAFAF8]/80 font-medium hover:text-[#FAFAF8] transition-colors text-base"
              data-testid="hero-login-btn"
            >
              Log In
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-6 border-t border-white/5" data-testid="stats-section">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { value: '82%', label: 'of critical knowledge lives in employees\' heads' },
            { value: '6 months', label: 'average recovery time after losing a key person' },
            { value: '$0', label: 'of it gets captured before they leave' },
          ].map(({ value, label }) => (
            <div key={value} className="text-center px-6 py-8 rounded-xl border border-white/5 bg-white/[0.02]">
              <p className="text-4xl sm:text-5xl font-bold text-[#3B82F6] mb-3 tracking-tight">{value}</p>
              <p className="text-sm text-[#FAFAF8]/50 leading-relaxed">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#3B82F6] mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Three steps. Zero disruption.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: Mic,
                title: 'Wear It & Work',
                desc: 'Your team records themselves doing their job — narrating as they go. No writing, no sitting at a desk.',
              },
              {
                step: '02',
                icon: Sparkles,
                title: 'AI Prompts Go Deeper',
                desc: 'Distill\'s AI asks follow-up questions to capture the nuance, the edge cases, the "you just know" stuff.',
              },
              {
                step: '03',
                icon: Database,
                title: 'Structured Into Your Company Brain',
                desc: 'Everything becomes searchable SOPs. New hires ask the Brain and get answers in seconds.',
              },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="relative p-8 rounded-xl border border-white/5 bg-white/[0.02] group hover:border-[#AAFF00]/20 transition-colors">
                <span className="text-xs font-mono text-[#AAFF00]/40 absolute top-6 right-6">{step}</span>
                <div className="w-12 h-12 rounded-lg bg-[#AAFF00]/10 flex items-center justify-center mb-5">
                  <Icon size={22} className="text-[#AAFF00]" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-sm text-[#FAFAF8]/50 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="quote-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8">
            <svg className="w-10 h-10 mx-auto text-[#3B82F6]/30" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
            </svg>
          </div>
          <blockquote className="text-2xl sm:text-3xl font-medium leading-snug tracking-tight mb-6">
            Every company in the world is going to need a company brain&hellip;
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-px h-5 bg-[#3B82F6]" />
            <p className="text-sm text-[#FAFAF8]/40">
              <span className="text-[#FAFAF8]/70 font-medium">Tom Blomfield</span> — Partner, Y Combinator
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
            Stop losing what your team knows.
          </h2>
          <p className="text-[#FAFAF8]/50 mb-8">Join the early access program and be the first to build your company brain.</p>
          <button
            onClick={() => navigate('/auth')}
            className="group inline-flex items-center gap-2 px-8 py-3.5 bg-[#AAFF00] text-[#080812] font-semibold rounded-md hover:bg-[#c4ff4d] transition-all text-base"
            data-testid="final-cta-btn"
          >
            Get Early Access
            <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-white/5" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <DistillLogo size="small" />
          <p className="text-xs text-[#FAFAF8]/30">&copy; {new Date().getFullYear()} Distill. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
